/**
 * BonBlockToggle — Bouton compact + modale d'admin pour SUSPENDRE temporairement
 * toutes les nouvelles demandes de bons personnel (employés et/ou managers).
 *
 * - Réservé à l'admin
 * - Preset de durées (1h / 3h / 8h / 24h / illimité)
 * - Choix du scope (Employés · Resp. Op. · Les deux)
 * - Motif optionnel archivé
 *
 * Quand un bloc est actif, le bouton devient rouge pulsant "BONS BLOQUÉS" avec
 * countdown. Un clic dessus permet de débloquer instantanément.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, ShieldOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DURATIONS = [
  { hours: 1, label: "1 heure" },
  { hours: 3, label: "3 heures" },
  { hours: 8, label: "8 heures" },
  { hours: 24, label: "24 heures" },
  { hours: 0, label: "Jusqu'à déblocage manuel" },
];

const SCOPES = [
  { id: "employee", label: "Employés uniquement" },
  { id: "manager", label: "Resp. Op. uniquement" },
  { id: "both", label: "Employés + Resp. Op." },
];

const BonBlockToggle = ({ adminName = "Admin", isAdmin = false, onChange, dataTestId = "bon-block-toggle" }) => {
  const [block, setBlock] = useState({ active: false });
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(2);
  const [scope, setScope] = useState("both");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState("");

  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/personnel-bon-block`);
      setBlock(res.data || { active: false });
      if (onChange) onChange(res.data || { active: false });
    } catch (e) { /* silencieux */ }
  }, [onChange]);

  // Fetch/countdown TOUJOURS actifs (même pour non-admin) pour que la bannière s'affiche à tous
  useEffect(() => { fetchState(); }, [fetchState]);

  // Compte à rebours live
  useEffect(() => {
    if (!block.active || !block.blocked_until) { setCountdown(""); return; }
    const tick = () => {
      const remaining = new Date(block.blocked_until) - new Date();
      if (remaining <= 0) { setCountdown("Expiré"); fetchState(); return; }
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      setCountdown(h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}m${String(s).padStart(2, "0")}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [block.active, block.blocked_until, fetchState]);

  // Rendu du bouton uniquement pour l'admin (la bannière est gérée séparément)
  if (!isAdmin) return null;

  const activate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/personnel-bon-block`, {
        scope, duration_hours: duration, reason, blocked_by: adminName,
      });
      toast.success("Blocage activé");
      setOpen(false); setReason("");
      await fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur activation blocage");
    } finally { setBusy(false); }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/personnel-bon-block`, { params: { unblocked_by: adminName } });
      toast.success("Blocage levé");
      await fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur déblocage");
    } finally { setBusy(false); }
  };

  if (block.active) {
    return (
      <Button
        onClick={deactivate}
        disabled={busy}
        className="h-9 bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-red-400 animate-pulse"
        data-testid={`${dataTestId}-active`}
        title={`Blocage actif — cliquer pour débloquer${block.blocked_until ? ` (expire dans ${countdown})` : ""}`}
      >
        <ShieldOff className="w-4 h-4 mr-1.5" />
        BONS BLOQUÉS
        {countdown && <span className="ml-2 text-[11px] font-mono opacity-90">· {countdown}</span>}
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="h-9 border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
        data-testid={dataTestId}
      >
        <Lock className="w-4 h-4 mr-1.5" /> Bloquer les demandes
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-slate-900 border-orange-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 32rem)" }}
          data-testid={`${dataTestId}-modal`}
        >
          <DialogHeader>
            <DialogTitle className="text-orange-300 flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5" />
              Bloquer les demandes de bons
            </DialogTitle>
            <p className="text-slate-400 text-[12px] pt-1">
              Suspend temporairement TOUTES les nouvelles créations de bons personnel.
              Les caissier·ère·s verront un message rouge "DEMANDES DE BONS TEMPORAIREMENT NON AUTORISÉES" à la tentative.
            </p>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Qui ?</label>
              <div className="mt-1.5 grid grid-cols-1 gap-1">
                {SCOPES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setScope(s.id)}
                    className={`text-left px-3 py-2 rounded-md text-[12px] border transition-colors ${
                      scope === s.id
                        ? "bg-orange-500/20 border-orange-500/60 text-orange-100 font-bold"
                        : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-orange-500/40"
                    }`}
                    data-testid={`${dataTestId}-scope-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Durée</label>
              <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-1">
                {DURATIONS.map(d => (
                  <button
                    key={d.hours}
                    onClick={() => setDuration(d.hours)}
                    className={`px-2 py-2 rounded-md text-[11.5px] border transition-colors ${
                      duration === d.hours
                        ? "bg-orange-500/20 border-orange-500/60 text-orange-100 font-bold"
                        : "bg-slate-800/60 border-slate-700 text-slate-300 hover:border-orange-500/40"
                    }`}
                    data-testid={`${dataTestId}-duration-${d.hours}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">Motif (optionnel, archivé)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex : dépassement budget mensuel, retour de fin de mois, audit en cours…"
                rows={2}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-orange-500/60 focus:outline-none"
                data-testid={`${dataTestId}-reason`}
              />
            </div>

            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
              <p className="text-amber-100 text-[11.5px] leading-snug">
                Le blocage est <b>immédiat</b>. Aucun bon ne pourra être créé pendant la durée choisie. Vous pourrez le lever à tout moment en cliquant sur le bouton "BONS BLOQUÉS".
              </p>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-slate-700">
            <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setOpen(false)} data-testid={`${dataTestId}-cancel`}>
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              onClick={activate}
              disabled={busy}
              data-testid={`${dataTestId}-confirm`}
            >
              <ShieldOff className="w-4 h-4 mr-1" /> Activer le blocage
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * BonBlockActiveBanner — Bandeau rouge affiché quand le blocage est actif.
 * À utiliser sur les pages Employé/Manager Orders au dessus de la liste.
 */
export const BonBlockActiveBanner = ({ block, dataTestId = "bon-block-banner" }) => {
  const [countdown, setCountdown] = useState("");
  useEffect(() => {
    if (!block?.blocked_until) { setCountdown(""); return; }
    const tick = () => {
      const remaining = new Date(block.blocked_until) - new Date();
      if (remaining <= 0) { setCountdown("Expiré"); return; }
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      setCountdown(h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}m`);
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => clearInterval(iv);
  }, [block?.blocked_until]);
  if (!block?.active) return null;
  return (
    <div
      className="rounded-lg border-2 border-red-500/70 bg-gradient-to-r from-red-900/50 via-red-950/40 to-slate-900/40 p-3 flex items-center gap-3 shadow-lg shadow-red-900/40"
      data-testid={dataTestId}
    >
      <div className="w-10 h-10 rounded-full bg-red-500/25 border border-red-500/60 flex items-center justify-center flex-shrink-0 animate-pulse">
        <ShieldOff className="w-5 h-5 text-red-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-red-100 text-[14px] font-black tracking-wider uppercase leading-tight">
          DEMANDES DE BONS TEMPORAIREMENT NON AUTORISÉES
        </p>
        <p className="text-red-300/85 text-[11px] mt-0.5">
          {block.blocked_until
            ? <>Blocage actif — expire dans <span className="font-mono font-bold text-red-100">{countdown}</span></>
            : <>Blocage actif — jusqu'à levée manuelle par l'administration</>}
          {block.reason && <span> · <i>{block.reason}</i></span>}
        </p>
      </div>
    </div>
  );
};

export default BonBlockToggle;
